import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpTable, Modal } from "../../components/erp";
import { errorText } from "../../api/client";
import { traceabilityApi, TraceabilityRow } from "./api";

const FIELDS = [
  { key: "FarmerName", label: "Farmer Name" }, { key: "AddressContinued", label: "Address" },
  { key: "Tracebility", label: "Traceability (Code/Pond, e.g. AMS-AP043910/001)" },
  { key: "RegAuthority", label: "Reg Authority" }, { key: "FarmPhoneNumber", label: "Phone Number" },
];

const TraceModal: React.FC<{ row?: TraceabilityRow; onClose: () => void }> = ({ row, onClose }) => {
  const [v, setV] = useState<Record<string, string>>({
    TraceID: row ? String(row.TraceID) : "", FarmerName: row?.FarmerName || "", AddressContinued: row?.AddressContinued || "",
    Tracebility: row?.Tracebility || "", RegAuthority: row?.RegAuthority || "", FarmPhoneNumber: row?.FarmPhoneNumber || "",
  });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { const r = await traceabilityApi.post(v); if (r.Error) setErr(r.Error); else onClose(); }
    catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={row ? "Edit Traceability" : "Create Traceability"} onClose={onClose} testId="traceability-modal">
      <form onSubmit={submit} data-testid="traceability-form">
        {FIELDS.map((f) => (
          <div className="mb-3" key={f.key}><label className="form-label">{f.label}</label>
            <input required={f.key === "FarmerName" || f.key === "Tracebility"} className="form-control" value={v[f.key] || ""}
              onChange={(e) => setV((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`traceability-${f.key}`} /></div>
        ))}
        {err && <div className="alert alert-danger" data-testid="traceability-error">{err}</div>}
        <button className="btn btn-success" type="submit" disabled={saving} data-testid="traceability-submit">{saving ? "Saving..." : "Submit"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/QualityControl/TraceabilityList.php (list) + CreateTraceability.php (create/edit) —
 * farm/pond traceability registry (table QcTracebilityList, 4000+ real rows in QA). */
export default function TraceabilityList() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const list = useQuery({ queryKey: ["qc", "traceability", search], queryFn: () => traceabilityApi.list(search || undefined) });
  const [editing, setEditing] = useState<TraceabilityRow | "new" | null>(null);

  return (
    <>
      <PageHeader title="Traceability" location="Quality Control" />
      <div className="card"><div className="card-body">
        <div className="row rowFilter mb-3">
          <div className="col-md-4">
            <input className="form-control" placeholder="Search farmer / traceability code" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="traceability-search" />
          </div>
          <div className="col-md-3">
            <button type="button" className="btn btn-success" onClick={() => setEditing("new")} data-testid="btn-new-traceability">
              <i className="fa fa-plus" /> New Traceability
            </button>
          </div>
        </div>
        {list.data && (list.data.Data || []).length >= 200 && (
          <Alert kind="info" testId="traceability-limit-note">Showing first 200 matches — refine your search for more specific results.</Alert>
        )}
        <ErpTable<TraceabilityRow> testId="traceability-table" loading={list.isLoading}
          error={list.isError ? errorText(list.error) : list.data?.Error || null} rows={list.data?.Data || []}
          columns={[
            { key: "FarmerName", title: "Farmer Name" }, { key: "AddressContinued", title: "Address" },
            { key: "Tracebility", title: "Traceability" }, { key: "PondNumber", title: "Pond" },
            { key: "RegAuthority", title: "Reg Authority" }, { key: "FarmPhoneNumber", title: "Phone" },
            { key: "TraceID", title: "Action", render: (r) => (
              <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditing(r)} data-testid={`edit-traceability-${r.TraceID}`}>Edit</button>
            ) },
          ]} />
      </div></div>
      {editing && <TraceModal row={editing === "new" ? undefined : editing} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["qc", "traceability"] }); }} />}
    </>
  );
}
